import { FC, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Col } from 'reactstrap';
import { Organization, Person } from 'types';
import { PageSection } from '../components/PageSection';
import PersonProfileIntroSummary, {
  PERSON_PROFILE_COMPONENT_BUSINESS_UNIT,
  PERSON_PROFILE_COMPONENT_DIRECT_REPORTS,
  PERSON_PROFILE_COMPONENT_JOINED,
  PERSON_PROFILE_COMPONENT_MANAGER,
  PERSON_PROFILE_COMPONENT_PREFERRED_FIRST_NAME,
} from '../PersonProfileIntroSummary';
import FeedbackProviderListWidget from './FeedbackProviderListWidget';
import PerformanceWrittenResponses from './PerformanceWrittenResponses';
import PersonPerformanceContext from './PersonPerformanceContext';
import { PerformanceHistoryData } from './Trajectory/HistoryChartUtils';
import ManagerRatingDistributionChart from './Trajectory/ManagerRatingDistributionChart';
import ManagerRatingHistoryChart from './Trajectory/ManagerRatingHistoryChart';
import { FormattedResponse } from './utils';

export interface Props {
  currentOrganization?: Organization;
  person: Person;
  manager?: Person;
  isInReviewFlow: boolean;
  displayManagerRatingDistribution: boolean;
  hideRatingFromDirectReport: boolean;
  showManagerOnlyPerformanceDetails: boolean;
  managerWrittenResponses?: FormattedResponse[];
  performanceHistory: PerformanceHistoryData[] | null | undefined;
}

const ManagerFeedbackSection: FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const context = useContext(PersonPerformanceContext);

  const profileInfoRenderComponents = useCallback((components) => {
    return (
      <>
        <Col sm={6}>
          {components[PERSON_PROFILE_COMPONENT_PREFERRED_FIRST_NAME]}
          {components[PERSON_PROFILE_COMPONENT_JOINED]}
        </Col>
        <Col sm={6} className="mb-4">
          {components[PERSON_PROFILE_COMPONENT_MANAGER]}
          {components[PERSON_PROFILE_COMPONENT_DIRECT_REPORTS]}
          {components[PERSON_PROFILE_COMPONENT_BUSINESS_UNIT]}
        </Col>
      </>
    );
  }, []);

  const managerSectionLayout = useMemo(() => {
    return {
      'manager-rating-distribution':
        context.displayCharts &&
        context.displayManagerDistribution &&
        props.displayManagerRatingDistribution ? (
          <ManagerRatingDistributionChart
            person={props.person}
            isInReviewFlow={props.isInReviewFlow}
            hideRatingFromDirectReport={props.hideRatingFromDirectReport}
          />
        ) : null,
      'manager-info': (
        <div
          className="position-sticky"
          style={{ top: '24px', marginBottom: '48px' }}
        >
          <PersonProfileIntroSummary
            title={formatMessage(
              {
                id: 'app.views.person.person_performance.title.manager_this_cycle',
                defaultMessage: "{name}'s manager this cycle",
              },
              {
                name: props.person.given_name,
              }
            )}
            fetchPersonDetails={true}
            showPersonHeader={true}
            isExternalUrl={true}
            isMe={false}
            isOpen={false}
            // @ts-expect-error
            person={props.manager}
            showOperatingManualLink={false}
            renderParts={profileInfoRenderComponents}
          />
        </div>
      ),
      'manager-rating-history':
        context.displayCharts && context.managerRatingHistoricalData ? (
          <ManagerRatingHistoryChart
            person={props.person}
            showManagerOnlyPerformanceDetails={
              props.showManagerOnlyPerformanceDetails
            }
            // @ts-expect-error
            performanceData={props.performanceHistory}
            // @ts-expect-error
            organization={props.currentOrganization}
          />
        ) : null,
      'written-responses': (
        <PerformanceWrittenResponses
          // @ts-expect-error
          person={props.manager}
          // @ts-expect-error
          questions={props.managerWrittenResponses}
        />
      ),
    };
  }, [
    formatMessage,
    props.manager,
    props.currentOrganization,
    context.displayCharts,
    context.displayManagerDistribution,
    context.managerRatingHistoricalData,
    props.displayManagerRatingDistribution,
    props.isInReviewFlow,
    props.person,
    props.hideRatingFromDirectReport,
    props.showManagerOnlyPerformanceDetails,
    props.managerWrittenResponses,
    props.performanceHistory,
    profileInfoRenderComponents,
  ]);

  const managerSectionComponents = useMemo(() => {
    return [
      ...(context.displayCharts &&
      (context.displayManagerDistribution || context.displayOnaDistribution)
        ? // both charts
          context.displayManagerDistribution &&
          props.displayManagerRatingDistribution &&
          context.managerRatingHistoricalData
          ? [
              {
                size: { xs: 12, lg: 6 },
                component: managerSectionLayout['manager-rating-distribution'],
              },
              {
                size: { xs: 12, lg: 6 },
                component: managerSectionLayout['manager-rating-history'],
              },
            ]
          : // only distribution
            context.displayManagerDistribution &&
              props.displayManagerRatingDistribution
            ? [
                {
                  size: { xs: 12, lg: 6 },
                  component:
                    managerSectionLayout['manager-rating-distribution'],
                },
                {
                  size: { xs: 12, lg: 6 },
                  component: managerSectionLayout['manager-info'],
                },
              ]
            : // only history
              context.managerRatingHistoricalData
              ? [
                  {
                    size: { xs: 12, lg: 6 },
                    component: managerSectionLayout['manager-rating-history'],
                  },
                  {
                    size: { xs: 12, lg: 6 },
                    component: managerSectionLayout['manager-info'],
                  },
                ]
              : []
        : []),
      ...(!context.displayCharts ||
      (context.displayCharts &&
        !context.displayManagerDistribution &&
        !context.displayOnaDistribution) ||
      (context.displayManagerDistribution &&
        props.displayManagerRatingDistribution &&
        context.managerRatingHistoricalData)
        ? [
            {
              size: { xs: 12, lg: 8 },
              component: managerSectionLayout['written-responses'],
            },
            {
              size: { xs: 12, lg: 4 },
              component: managerSectionLayout['manager-info'],
            },
          ]
        : [
            {
              size: { xs: 12, lg: 12 },
              component: managerSectionLayout['written-responses'],
            },
          ]),
    ];
  }, [
    managerSectionLayout,
    props.displayManagerRatingDistribution,
    context.displayOnaDistribution,
    context.displayCharts,
    context.displayManagerDistribution,
    context.managerRatingHistoricalData,
  ]);

  return (
    <PageSection
      id="perf-profile-manager-feedback"
      title={formatMessage({
        id: 'app.views.person.person_performance.title.manager_feedback',
        defaultMessage: 'Manager feedback',
      })}
      titleIcon="user"
      titleIconColor="text-success"
      widths={managerSectionComponents.map((x) => x.size)}
      rightWidget={
        // @ts-expect-error
        <FeedbackProviderListWidget people={[props.manager]} />
      }
    >
      {managerSectionComponents.map((x) => x.component)}
    </PageSection>
  );
};

export default ManagerFeedbackSection;
